import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { exportService, downloadBlob, KpiData } from '@/services/exportService';
import toast from 'react-hot-toast';
import {
  DocumentArrowDownIcon,
  ChartBarIcon,
  CurrencyEuroIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

/**
 * Page des exports et rapports
 */
export function ExportsPage() {
  const today = new Date().toISOString().split('T')[0];
  const [dateDebut, setDateDebut] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [dateFin, setDateFin] = useState(today);
  const [rapportDate, setRapportDate] = useState(today);
  const [loading, setLoading] = useState<string | null>(null);

  // KPI
  const { data: kpi, isLoading: kpiLoading } = useQuery({
    queryKey: ['kpi', dateDebut, dateFin],
    queryFn: () => exportService.getKpi({ dateDebut, dateFin }),
  });

  // Rapport journalier
  const { data: rapport, isLoading: rapportLoading } = useQuery({
    queryKey: ['rapport-journalier', rapportDate],
    queryFn: () => exportService.getRapportJournalier(rapportDate),
  });

  const handleDownloadFacturation = async () => {
    setLoading('facturation');
    try {
      const blob = await exportService.downloadFacturationCsv({ dateDebut, dateFin });
      downloadBlob(blob, `facturation_${dateDebut}_${dateFin}.csv`);
      toast.success('Export facturation telecharge');
    } catch {
      toast.error('Erreur lors du telechargement');
    } finally {
      setLoading(null);
    }
  };

  const handleDownloadInterventions = async () => {
    setLoading('interventions');
    try {
      const blob = await exportService.downloadInterventionsCsv({ dateDebut, dateFin });
      downloadBlob(blob, `interventions_${dateDebut}_${dateFin}.csv`);
      toast.success('Export interventions telecharge');
    } catch {
      toast.error('Erreur lors du telechargement');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Exports et Rapports</h2>
      </div>

      {/* Filtres periode */}
      <div className="card">
        <div className="card-body">
          <h3 className="font-semibold mb-4">Periode d'analyse</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Date debut</label>
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="label">Date fin</label>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                className="input"
              />
            </div>
            <div className="flex items-end space-x-2">
              <button
                onClick={() => {
                  setDateDebut(
                    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .split('T')[0]
                  );
                  setDateFin(today);
                }}
                className="btn-secondary"
              >
                7 jours
              </button>
              <button
                onClick={() => {
                  setDateDebut(
                    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                      .toISOString()
                      .split('T')[0]
                  );
                  setDateFin(today);
                }}
                className="btn-secondary"
              >
                30 jours
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center">
              <ChartBarIcon className="w-5 h-5 mr-2 text-primary-500" />
              Indicateurs cles (KPI)
            </h3>
          </div>

          {kpiLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              <p className="text-sm text-gray-500 mt-3">Chargement des KPI...</p>
            </div>
          ) : kpi ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-gray-900">{kpi.totalInterventions}</p>
                <p className="text-sm text-gray-500">Interventions</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{kpi.validees}</p>
                <p className="text-sm text-gray-500">Validees</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">
                  {kpi.tempsMoyenMinutes} min
                </p>
                <p className="text-sm text-gray-500">Temps moyen</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">
                  {(kpi.tauxValidation * 100).toFixed(1)}%
                </p>
                <p className="text-sm text-gray-500">Taux validation</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Actions d'export */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Export Facturation */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <CurrencyEuroIcon className="w-8 h-8 text-primary-500 mr-3" />
              <div>
                <h3 className="font-semibold">Export Facturation</h3>
                <p className="text-sm text-gray-500">
                  Donnees de facturation par compagnie (R9)
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Inclut: nombre d'interventions, durees
            </p>
            <button
              onClick={handleDownloadFacturation}
              disabled={loading === 'facturation'}
              className="btn-primary w-full"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
              {loading === 'facturation' ? 'Telechargement...' : 'Telecharger CSV'}
            </button>
          </div>
        </div>

        {/* Export Interventions */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center mb-4">
              <ClipboardDocumentListIcon className="w-8 h-8 text-primary-500 mr-3" />
              <div>
                <h3 className="font-semibold">Export Interventions</h3>
                <p className="text-sm text-gray-500">Liste detaillee des interventions</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Inclut: toutes les interventions avec statut, horaires, equipes
            </p>
            <button
              onClick={handleDownloadInterventions}
              disabled={loading === 'interventions'}
              className="btn-primary w-full"
            >
              <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
              {loading === 'interventions' ? 'Telechargement...' : 'Telecharger CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* Rapport journalier */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center">
              <CalendarDaysIcon className="w-5 h-5 mr-2 text-primary-500" />
              Rapport journalier
            </h3>
            <input
              type="date"
              value={rapportDate}
              onChange={(e) => setRapportDate(e.target.value)}
              className="input w-auto"
            />
          </div>

          {rapportLoading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              <p className="text-sm text-gray-500 mt-3">Chargement du rapport...</p>
            </div>
          ) : rapport ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    {rapport.totalInterventions}
                  </p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{rapport.planifiees}</p>
                  <p className="text-xs text-gray-500">Planifiees</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-600">{rapport.enCours}</p>
                  <p className="text-xs text-gray-500">En cours</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-lime-600">{rapport.terminees}</p>
                  <p className="text-xs text-gray-500">Terminees</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{rapport.validees}</p>
                  <p className="text-xs text-gray-500">Validees</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-400">{rapport.annulees}</p>
                  <p className="text-xs text-gray-500">Annulees</p>
                </div>
              </div>

              {rapport.interventions?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="table text-sm">
                    <thead>
                      <tr>
                        <th>N° Ordre</th>
                        <th>Compagnie</th>
                        <th>Parkings</th>
                        <th>Statut</th>
                        <th>Debut</th>
                        <th>Fin</th>
                        <th>Duree</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rapport.interventions.map((inter, idx) => (
                        <tr key={idx}>
                          <td className="font-medium">{inter.numeroOrdre}</td>
                          <td>{inter.compagnie}</td>
                          <td>{inter.parkings}</td>
                          <td>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                inter.statut === 'validee'
                                  ? 'bg-green-100 text-green-800'
                                  : inter.statut === 'en_cours'
                                  ? 'bg-orange-100 text-orange-800'
                                  : inter.statut === 'cloturee'
                                  ? 'bg-lime-100 text-lime-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {inter.statut}
                            </span>
                          </td>
                          <td>{inter.heureDebut || '-'}</td>
                          <td>{inter.heureFin || '-'}</td>
                          <td>{inter.duree ? `${inter.duree} min` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {rapport.interventions?.length === 0 && (
                <p className="text-center text-gray-500 py-4">
                  Aucune intervention pour cette date
                </p>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ExportsPage;
